import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PageShell from "@/components/PageShell";
import KalkiMark from "@/components/KalkiMark";

const chips = [
  { id: "purpose", emoji: "🌟", label: "Ask about my purpose", prompt: "KALKI, help me discover my purpose." },
  { id: "career", emoji: "💼", label: "Guide my career", prompt: "KALKI, guide my career today." },
  { id: "peace", emoji: "🧘", label: "Help me stay peaceful", prompt: "KALKI, help me stay peaceful when life feels loud." },
  { id: "courage", emoji: "🛡️", label: "Teach me courage", prompt: "KALKI, teach me courage in this moment." },
  { id: "today", emoji: "☀️", label: "What should I do today?", prompt: "KALKI, what should I do today?" },
  { id: "love", emoji: "❤️", label: "Help me understand love", prompt: "KALKI, help me understand love." },
  { id: "inspire", emoji: "✨", label: "Inspire me today", prompt: "KALKI, inspire me today with something wise." },
];

export default function Landing() {
  const navigate = useNavigate();
  const startWith = (prompt) => navigate("/chat", { state: { seed: prompt } });

  return (
    <PageShell>
      <main
        data-testid="landing"
        className="relative flex flex-col items-center justify-center flex-1 px-6 sm:px-10 py-16 sm:py-24"
      >
        {/* Halo behind logo */}
        <div
          aria-hidden
          className="kalki-halo"
          style={{ top: "8vh", left: "50%", transform: "translateX(-50%)", width: 520, height: 520 }}
        />

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="relative z-10 flex flex-col items-center text-center max-w-3xl"
        >
          <KalkiMark />

          <h1
            data-testid="kalki-title"
            className="font-display mt-8 text-6xl sm:text-7xl md:text-8xl font-extralight tracking-[-0.05em] text-neutral-950 leading-none"
          >
            KALKI
          </h1>

          <div className="mt-3 flex items-center gap-3">
            <div className="gold-hairline w-10" />
            <span className="text-[11px] sm:text-[12px] font-medium tracking-[0.42em] uppercase gold-text">
              Talk to KALKI · Find Clarity
            </span>
            <div className="gold-hairline w-10" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mt-10 text-neutral-500 max-w-xl text-[15px] sm:text-[16px] leading-relaxed space-y-3"
          >
            <p className="text-neutral-900 font-medium text-[16px] sm:text-[17px]">
              Hi, I am KALKI, your friend.
            </p>
            <p>
              I am here with you. Whether you&rsquo;re celebrating something beautiful,
              chasing a dream, making a hard choice, or simply wishing to talk &mdash;
              come, let us walk this together.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.28 }}
            className="mt-10"
          >
            <button
              data-testid="begin-conversation-btn"
              onClick={() => navigate("/chat")}
              className="group liquid-gold relative overflow-hidden inline-flex items-center gap-2.5 rounded-full bg-neutral-950 text-white text-[14px] font-medium tracking-wide px-8 py-4 transition-all hover:bg-neutral-800 active:scale-[0.98] shadow-[0_20px_50px_-20px_rgba(197,160,89,0.65)]"
            >
              <span className="relative z-10">Start Conversation</span>
              <ArrowRight className="w-4 h-4 relative z-10 transition-transform group-hover:translate-x-0.5" />
            </button>
          </motion.div>
        </motion.div>

        {/* Suggestion chips */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.45 }}
          className="relative z-10 mt-16 w-full max-w-5xl"
        >
          <div className="flex items-center gap-3 justify-center mb-6">
            <div className="gold-hairline w-8" />
            <span className="text-[10px] font-semibold tracking-[0.35em] uppercase text-neutral-400">
              Or start with
            </span>
            <div className="gold-hairline w-8" />
          </div>

          <div className="flex flex-wrap justify-center gap-2.5 sm:gap-3">
            {chips.map((c, i) => (
              <motion.button
                key={c.id}
                data-testid={`chip-${c.id}`}
                onClick={() => startWith(c.prompt)}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.55 + i * 0.05 }}
                whileHover={{ y: -2 }}
                className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white/80 backdrop-blur px-4 py-2.5 text-[13px] font-medium text-neutral-700 transition-all hover:border-[#E4C98A] hover:text-neutral-950 hover:shadow-[0_10px_30px_-15px_rgba(197,160,89,0.6)]"
              >
                <span aria-hidden>{c.emoji}</span>
                <span>{c.label}</span>
              </motion.button>
            ))}
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="relative z-10 mt-16 text-center text-[11px] tracking-widest uppercase text-neutral-300"
        >
          Your personal Vedic companion · No login needed
        </motion.p>
      </main>
    </PageShell>
  );
}
